import type { CgOrderType } from '../cgOrders/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';

/**
 * Raising a CG order from a deal.
 *
 * The last piece of batch 3: a deal that closes won has to become an order,
 * and until now that meant retyping the client, the company and the contact
 * into the New Order drawer and hoping you picked the same company row the
 * deal was already pointing at.
 *
 * ── Why this does not check that the deal is won ───────────────────────────
 *
 * Neither does the database, and for the same reason. Requiring `won` would
 * leave somebody who needs the order raised at Contract Signed with two
 * choices: mark the deal won before it is, or work outside the Hub. The first
 * corrupts the outcome field this batch exists to make trustworthy.
 *
 * The RPC echoes the deal's outcome back so the screen can say "this deal is
 * still open" without a second round trip to find out what it already knew.
 */

export interface OrderFromDeal {
  order_id: string;
  order_no: string;
  client: string;
  /** Echoed back so the confirmation can name the pipeline it actually got. */
  order_type: CgOrderType;
  deal_outcome: 'open' | 'won' | 'lost';
}

export interface OrderFromDealInput {
  dealId: string;
  /** Blank asks the database for the next number. */
  orderNo?: string;
  clientName?: string;
  /**
   * Which pipeline to raise it into. Omitted means custom, which is what every
   * caller meant before the second pipeline existed.
   *
   * Kyu: "the type decision happens at the handoff, not after the wrong
   * workflow has started."
   */
  orderType?: CgOrderType;
}

/**
 * The RPC parameters, built where they can be tested.
 *
 * This was inline in the mutation, and the only guard on it was a test
 * matching the source text character for character. That guard could not tell
 * the difference between reformatting and dropping a parameter — and dropping
 * p_order_type would send every In-Stock order raised from a deal into the
 * custom pipeline, silently.
 *
 * Every optional argument is OMITTED rather than sent empty: blank means
 * "allocate the next number", and '' would fail the NOT NULL check while null
 * would too. The parameter has to be absent.
 */
export interface OrderFromDealArgs {
  p_deal: string;
  p_order_no?: string;
  p_client?: string;
  p_order_type?: string;
}

export function orderFromDealArgs(input: OrderFromDealInput): OrderFromDealArgs {
  const args: OrderFromDealArgs = { p_deal: input.dealId };
  if (input.orderNo !== undefined && input.orderNo.trim() !== '') {
    args.p_order_no = input.orderNo;
  }
  if (input.clientName !== undefined && input.clientName.trim() !== '') {
    args.p_client = input.clientName;
  }
  if (input.orderType !== undefined) {
    args.p_order_type = input.orderType;
  }
  return args;
}

export function useCreateOrderFromDeal() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: OrderFromDealInput): Promise<OrderFromDeal> => {
      const { data, error } = await client.rpc('create_order_from_deal', orderFromDealArgs(input));
      if (error) {
        throw new Error(error.message);
      }
      return data as unknown as OrderFromDeal;
    },
    onSettled: () => {
      // The CG board is a different module with its own keys; invalidating the
      // whole cache is heavy-handed, so this only clears what it can name.
      void queryClient.invalidateQueries({ queryKey: ['cg'] });
    },
  });
}

/**
 * The database's refusals, in words somebody can act on.
 *
 * The two permission messages are separated on purpose. "You are not on the CG
 * team" and "your role does not include CG Orders" send somebody to different
 * people, and collapsing them into "no access" wastes a round trip through
 * whoever they ask first.
 */
export function orderFromDealErrorMessage(raw: string): string {
  if (raw.includes('not_authorized_for_cg_orders')) {
    return 'You are not on the CG Orders team, so you cannot raise an order. Ask a TAP admin to add you.';
  }
  if (raw.includes('cg_orders_module_denied')) {
    return 'Your role does not include CG Orders. Ask a TAP admin to grant it in the permissions matrix.';
  }
  if (raw.includes('order_no_taken')) {
    return 'That order number already exists. Leave it blank to take the next one.';
  }
  if (raw.includes('deal_not_found')) {
    return 'That deal no longer exists. Refresh the page.';
  }
  if (raw.includes('not_signed_in')) {
    return 'Your session has expired. Sign in again.';
  }
  if (raw.includes('client_approval_required')) {
    return 'A new order cannot start past the client approval gate.';
  }
  // Refused rather than defaulted, on purpose: an order quietly raised into
  // the wrong pipeline is found weeks later by somebody wondering why a stock
  // order is asking for a PO.
  if (raw.includes('unknown_order_type')) {
    return 'That is not a pipeline. Choose Custom or In-Stock and try again.';
  }
  return 'The order could not be created. Try again.';
}
